import React from 'react';

interface TimeEntry {
  date?: string;
  status?: string;
  key?: string;
  time: string;
  time_seconds: number;
  summary?: string;
}

interface AnalyticsData {
  total_time: string;
  total_time_seconds: number;
  by_ticket?: TimeEntry[];
  by_day?: TimeEntry[];
  by_status?: TimeEntry[];
  avg_time_per_ticket?: string;
  ticket_count: number;
}

interface AnalyticsCardProps {
  data: AnalyticsData;
  period: string;
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({ data, period }) => {
  const getPeriodLabel = () => {
    switch (period) {
      case 'day': return 'Dnes';
      case 'week': return 'Tento týden';
      case 'month': return 'Tento měsíc';
      default: return period;
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4 border border-blue-200 dark:border-gray-600 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Time Tracking - {getPeriodLabel()}
        </h3>
        <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
          {data.total_time}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white dark:bg-gray-600 rounded p-3 shadow-sm">
          <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">Tickets</div>
          <div className="text-xl font-bold text-gray-800 dark:text-white">{data.ticket_count}</div>
        </div>
        <div className="bg-white dark:bg-gray-600 rounded p-3 shadow-sm">
          <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">Průměr/ticket</div>
          <div className="text-xl font-bold text-gray-800 dark:text-white">{data.avg_time_per_ticket || '0h'}</div>
        </div>
      </div>

      {/* By Ticket */}
      {data.by_ticket && data.by_ticket.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Tickets</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data.by_ticket.slice(0, 5).map((ticket, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-600 rounded p-2 flex items-center justify-between text-xs">
                <div className="flex-1 min-w-0">
                  <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">{ticket.key}</span>
                  {ticket.summary && (
                    <div className="text-gray-600 dark:text-gray-300 truncate">{ticket.summary}</div>
                  )}
                </div>
                <span className="ml-2 font-semibold text-gray-800 dark:text-white whitespace-nowrap">{ticket.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Status */}
      {data.by_status && data.by_status.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Podle Statusu</h4>
          <div className="space-y-2">
            {data.by_status.map((status, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-600 rounded p-2 flex items-center justify-between text-xs">
                <span className="text-gray-700 dark:text-gray-300">{status.status}</span>
                <span className="font-semibold text-gray-800 dark:text-white">{status.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Day (for week/month view) */}
      {data.by_day && data.by_day.length > 0 && period !== 'day' && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Denní Trend</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {data.by_day.slice(-7).map((day, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">{day.date}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 dark:bg-gray-500 rounded-full h-2">
                    <div
                      className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full"
                      style={{ width: `${Math.min((day.time_seconds / (8 * 3600)) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="font-semibold text-gray-800 dark:text-white w-12 text-right">{day.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsCard;
